import React, { useState } from 'react';
import { Report, ReportCoverPageParams } from 'waypoint-types';
import { Checkbox, Select } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useGetReportSettings } from 'waypoint-hooks';
import { WidgetTypes } from '../constants';

interface ReportSettingsProps {
    report?: Report;
    includeCoverPage: boolean;
    setIncludeCoverPage: React.Dispatch<React.SetStateAction<boolean>>;
    coverPageSettings: { [x: string]: any } | null;
    setCoverPageSettings: React.Dispatch<
        React.SetStateAction<{ [x: string]: any } | null>
    >;
}

const alignStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
};

const ReportSettings = ({
    report,
    includeCoverPage,
    setIncludeCoverPage,
    coverPageSettings,
    setCoverPageSettings,
}: ReportSettingsProps) => {
    const { data } = useGetReportSettings();
    const settings = report?.reportSettings.find(
        (setting) => setting.settings_type === WidgetTypes.CoverPage
    );

    const defaultIncludeCoverPage = settings?.enabled ?? false;

    const onChangePageEnabled = (e: CheckboxChangeEvent) => {
        setIncludeCoverPage(e.target.checked);
        if (e.target.checked && !coverPageSettings) {
            setCoverPageSettings({
                paperSize: settings?.settings?.paperSize ?? 'letter',
                paperOrientation:
                    settings?.settings?.paperOrientation ?? 'portrait',
                propertyPhoto: settings?.settings?.propertyPhoto ?? false,
                dueDate: settings?.settings?.dueDate ?? false,
                assignees: settings?.settings?.assignees ?? false,
                reviewers: settings?.settings?.reviewers ?? false,
            });
        }
    };

    const handleCheckboxChange =
        (key: keyof ReportCoverPageParams) => (e: CheckboxChangeEvent) => {
            if (coverPageSettings) {
                setCoverPageSettings((prevState) => {
                    if (!prevState) {
                        return null;
                    }
                    return {
                        ...prevState,
                        [key]: e.target.checked,
                    };
                });
            }
        };

    return (
        <div
            style={{
                border: '1px solid #f0f0f0',
                padding: 16,
                borderRadius: 8,
            }}
        >
            <div style={{ marginTop: '12px' }}>
                <h2>Cover Page Settings</h2>
                <div style={alignStyle}>
                    <Checkbox
                        defaultChecked={defaultIncludeCoverPage}
                        onChange={onChangePageEnabled}
                    >
                        Include Cover Page
                    </Checkbox>
                    <div style={alignStyle}>
                        <div>
                            <Select
                                disabled={!includeCoverPage}
                                style={{
                                    width: '125px',
                                    marginRight: '12px',
                                }}
                                size={'small'}
                                defaultValue={
                                    settings?.settings?.paperOrientation ??
                                    data?.paperOrientation[0].value
                                }
                                options={data?.paperOrientation ?? []}
                                onChange={(value) => {
                                    if (coverPageSettings) {
                                        setCoverPageSettings({
                                            ...coverPageSettings,
                                            paperOrientation: value as string,
                                        });
                                    }
                                }}
                            />
                        </div>
                        <div>
                            <Select
                                disabled={!includeCoverPage}
                                style={{
                                    width: '125px',
                                }}
                                size={'small'}
                                defaultValue={
                                    settings?.settings?.paperSize ??
                                    data?.paperSize[0].value
                                }
                                options={data?.paperSize ?? []}
                                onChange={(value) => {
                                    if (coverPageSettings) {
                                        setCoverPageSettings({
                                            ...coverPageSettings,
                                            paperSize: value as string,
                                        });
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div
                style={{
                    border: '1px solid #f0f0f0',
                    padding: 16,
                    marginTop: '12px',
                    borderRadius: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    opacity: includeCoverPage ? 1 : 0.5,
                    pointerEvents: includeCoverPage ? 'auto' : 'none',
                    cursor: includeCoverPage ? 'auto' : 'not-allowed',
                }}
            >
                <div style={{ marginTop: '6px' }}>
                    <Checkbox disabled checked>
                        Report Name
                    </Checkbox>
                </div>
                <div style={{ marginTop: '6px' }}>
                    <Checkbox disabled checked>
                        Address
                    </Checkbox>
                </div>
                <div style={{ marginTop: '6px' }}>
                    <Checkbox disabled checked>
                        Property Name
                    </Checkbox>
                </div>
                <div style={{ marginTop: '6px' }}>
                    <Checkbox
                        defaultChecked={settings?.settings?.propertyPhoto}
                        onChange={handleCheckboxChange('propertyPhoto')}
                    >
                        Property Photo
                    </Checkbox>
                </div>
                <div style={{ marginTop: '6px' }}>
                    <Checkbox
                        defaultChecked={settings?.settings?.dueDate}
                        onChange={handleCheckboxChange('dueDate')}
                    >
                        Due Date
                    </Checkbox>
                </div>
                <div style={{ marginTop: '6px' }}>
                    <Checkbox
                        defaultChecked={settings?.settings?.assignees}
                        onChange={handleCheckboxChange('assignees')}
                    >
                        Assignees
                    </Checkbox>
                </div>
                <div style={{ marginTop: '6px' }}>
                    <Checkbox
                        defaultChecked={settings?.settings?.reviewers}
                        onChange={handleCheckboxChange('reviewers')}
                    >
                        Reviewers
                    </Checkbox>
                </div>
            </div>
        </div>
    );
};

export default ReportSettings;
